
import {MovieLayer} from 'common/src/modules/movie/movieLayers.types';
import {MovieComp} from 'common/src/modules/movie/movie.types';
import {Box} from '../../movieEditor/movieEditor.types';
import getLayerTotalTransform from '../../movieEditor/utils/getLayerTotalTransform';
import applyKeyframe from './applyKeyframe';
import getDefaultPerspective from '../../movieEditor/utils/getDefaultPerspective';
import transformPoint from 'common/src/modules/movieEditor/utils/transformPoint';
import {getLayerBoundingBox} from './getLayerBoundingBox';

function getLayerBoundingBoxRelativeToComposition(
  layer: MovieLayer,
  comp: MovieComp,
  comps: Record<string, MovieComp>,
  currentTime: number,
): Box {
  if (!layer || !comp) return null;

  // Get local bounding box - need to apply keyframe for this
  const timedLayer = applyKeyframe(layer, currentTime);
  const localBox = getLayerBoundingBox(timedLayer, comps);
  if (!localBox) return null;

  // Get total transform matrix - use original layer since getLayerTotalTransform handles keyframes internally
  const transformMatrix = getLayerTotalTransform(comp, layer, currentTime);
  if (!transformMatrix) return localBox;

  // For 3D layers, we need to apply camera matrix
  const finalMatrix = layer.is3d 
    ? (() => {
        const xCenter = comp.w / 2;
        const yCenter = comp.h / 2;
        const zoom = getDefaultPerspective(comp.w);

        // Create camera matrix matching Layer component's CSS transform order
        const cameraMatrix = new DOMMatrix()
          // First translate to center
          .translateSelf(xCenter, yCenter, 0)
          // Apply perspective
          .multiplySelf(new DOMMatrix().setMatrixValue(`perspective(${zoom}px)`))
          // Translate back from center
          .translateSelf(-xCenter, -yCenter, 0)
          // Apply the layer's transform
          .multiplySelf(transformMatrix);

        return cameraMatrix;
      })()
    : transformMatrix;

  // Transform the four corners of the bounding box using transformPoint
  const topLeft = transformPoint({x: localBox.x, y: localBox.y}, finalMatrix);
  const topRight = transformPoint({x: localBox.x + localBox.w, y: localBox.y}, finalMatrix);
  const bottomLeft = transformPoint({x: localBox.x, y: localBox.y + localBox.h}, finalMatrix);
  const bottomRight = transformPoint({x: localBox.x + localBox.w, y: localBox.y + localBox.h}, finalMatrix);

  // Calculate the new bounding box from transformed points
  const x = Math.min(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
  const y = Math.min(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);
  const right = Math.max(topLeft.x, topRight.x, bottomLeft.x, bottomRight.x);
  const bottom = Math.max(topLeft.y, topRight.y, bottomLeft.y, bottomRight.y);

  return {
    x,
    y,
    w: right - x,
    h: bottom - y
  };
}

export {getLayerBoundingBoxRelativeToComposition}; 